import type { ComponentProps, ReactElement } from 'react'
import { useState } from 'react'

import { i18n } from '@lingui/core'
import { isNil } from 'ramda'
import { useFormContext } from 'react-hook-form'

import {
  getCallingCodeConfig,
  normalizePhoneNumber,
  validator,
} from '@twistopayments/design-system/form'
import type { CallingCode } from '@twistopayments/design-system/form'
import { FlagCzIcon, FlagPlIcon, FlagSkIcon } from '@twistopayments/design-system/icons/color'

import { FormHelperText } from '../../atoms/form-helper-text'
import { Input } from '../../atoms/input'
import { InputLabel } from '../../atoms/input-label'
import { NativeSelect } from '../../atoms/native-select'

import * as styles from './phone-number-field.styles'

const getFlagForCallingCode = (callingCode: string) => {
  switch (callingCode) {
    case '+420':
      return <FlagCzIcon />
    case '+421':
      return <FlagSkIcon />
    case '+48':
      return <FlagPlIcon />
    default:
      return null
  }
}

type Props = {
  name: string
  label?: string | ReactElement
  callingCodes: readonly CallingCode[] | string[]
  isRequired?: boolean
  helperText?: string | undefined | ReactElement
  hideFlagAdornment?: boolean
} & Pick<
  ComponentProps<typeof Input>,
  'error' | 'autoFocus' | 'disabled' | 'autoComplete' | 'readOnly'
>

export type PhoneNumberFieldFormValues = {
  callingCode: CallingCode | string
  nationalNumber: string
}

export const PhoneNumberField = ({
  name,
  label,
  callingCodes,
  helperText,
  error: errorProp = false,
  isRequired = false,
  hideFlagAdornment = false,
  disabled,
  ...others
}: Props) => {
  // results in name:{callingCode:'',nationalNumber:''}
  const callingCodeName = `${name}.callingCode`
  const nationalNumberName = `${name}.nationalNumber`

  const { formState, watch, clearErrors, register } = useFormContext()
  const fieldErrors = formState.errors

  const hasError =
    errorProp ||
    Boolean(fieldErrors[name]) ||
    Boolean(fieldErrors[callingCodeName]) ||
    Boolean(fieldErrors[nationalNumberName])
  const [isFocused, setIsFocused] = useState(false)

  const currentCallingCode = watch(callingCodeName) ?? callingCodes[0]
  const currentCallingCodeConfig = getCallingCodeConfig(currentCallingCode)

  const validate = (value: string) => {
    const isRequiredError = validator.formValidators.isRequired(value)
    if (isRequired && !isNil(isRequiredError)) {
      return isRequiredError
    }

    if (!isNil(currentCallingCodeConfig)) {
      return validator.formValidators.isValidPhoneNumber(
        value,
        currentCallingCodeConfig.format
      )
    }
  }

  const a11y = {
    /*i18n*/
    'arial-label': i18n._('form.fields.phoneNumber.ariaLabel'),
  }

  const callingCodeEditEnabled = callingCodes.length > 1

  const handleInputsBlur = () => setIsFocused(false)
  const handleInputsFocus = () => setIsFocused(true)

  return (
    <div css={styles.direction}>
      {!isNil(label) && <InputLabel>{label}</InputLabel>}
      <div css={styles.root}>
        {callingCodeEditEnabled && (
          <NativeSelect
            css={styles.select}
            disabled={disabled}
            error={hasError}
            focused={isFocused}
            hideArrow={true}
            name={callingCodeName}
            startAdornment={getFlagForCallingCode(currentCallingCode)}
            onBlur={handleInputsBlur}
            onFocus={handleInputsFocus}
          >
            {callingCodes.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </NativeSelect>
        )}

        {!callingCodeEditEnabled && (
          <input
            {...register(callingCodeName)}
            role="none"
            type="hidden"
            value={currentCallingCode}
          />
        )}

        <Input
          {...a11y}
          disabled={disabled}
          error={hasError}
          focused={isFocused}
          formattedOptions={currentCallingCodeConfig?.format}
          name={nationalNumberName}
          placeholder={currentCallingCodeConfig?.placeholder}
          rules={{ validate }}
          startAdornment={
            callingCodeEditEnabled ? undefined : (
              <span
                css={styles.countryCode}
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                {!hideFlagAdornment &&
                  getFlagForCallingCode(currentCallingCode)}
                {currentCallingCode}
              </span>
            )
          }
          styles={
            callingCodeEditEnabled
              ? {
                  startAdornment: styles.startAdornment,
                }
              : {
                  root: styles.input,
                  startAdornment: styles.countryCode,
                }
          }
          type="tel"
          onBlur={handleInputsBlur}
          onChange={(event) => {
            clearErrors(name)
            // removes spaces from "000 000 000"
            // eslint-disable-next-line no-param-reassign
            event.target.value = normalizePhoneNumber(event.target.value)
          }}
          onFocus={handleInputsFocus}
          {...others}
        />
      </div>
      <FormHelperText
        disabled={disabled}
        errors={fieldErrors}
        isError={hasError}
        name={nationalNumberName}
      >
        {helperText}
      </FormHelperText>
      <FormHelperText
        disabled={disabled}
        errors={fieldErrors}
        isError={hasError}
        name={name}
      >
        {helperText}
      </FormHelperText>
    </div>
  )
}
